export type HuntConstraintCode =
  | 'HUNT_CUTOFF_PASSED'
  | 'HUNT_CUTOFF_LOCKED'
  | 'TIME_BLOCK_NOT_ALLOWED'
  | 'MAX_ACTIVE_HUNTS_REACHED'
  | 'MAX_DAYS_OUT_EXCEEDED'
  | 'DAYS_PER_HUNT_NOT_MET'
  | 'LOCATION_OCCUPANCY_LIMIT_REACHED'
  | 'BIRD_MINIMUM_NOT_MET'
  | 'OVERAGE_CAP_EXCEEDED'
  | 'INSUFFICIENT_STOCK'
  | 'LOCATION_INACTIVE'

export type HuntConstraintWarning = {
  code: HuntConstraintCode
  message: string
  limit?: number
  actual?: number
  userId?: string
  userName?: string
}

const BOOKING_CONSTRAINT_CODES: HuntConstraintCode[] = [
  'HUNT_CUTOFF_PASSED',
  'HUNT_CUTOFF_LOCKED',
  'TIME_BLOCK_NOT_ALLOWED',
  'MAX_ACTIVE_HUNTS_REACHED',
  'MAX_DAYS_OUT_EXCEEDED',
  'DAYS_PER_HUNT_NOT_MET',
  'LOCATION_OCCUPANCY_LIMIT_REACHED',
  'BIRD_MINIMUM_NOT_MET',
  'OVERAGE_CAP_EXCEEDED',
  'INSUFFICIENT_STOCK',
  'LOCATION_INACTIVE',
]

const GENERIC_REFUSAL_MESSAGE =
  'This hunt breaks a booking rule for your organization. Ask a manager to book it for you.'

const REFUSAL_MESSAGES: Partial<Record<HuntConstraintCode, string>> = {
  HUNT_CUTOFF_PASSED:
    'Bookings for this hunt have already closed. Ask a manager to book it for you.',
  HUNT_CUTOFF_LOCKED:
    'This hunt starts inside the cutoff window. Contact a manager to cancel or change it.',
  TIME_BLOCK_NOT_ALLOWED:
    'That time block is not open for booking. Pick another time, or ask a manager to book it for you.',
  MAX_ACTIVE_HUNTS_REACHED:
    'You have reached your limit of active hunts. Cancel or shorten one, or ask a manager to book this for you.',
  MAX_DAYS_OUT_EXCEEDED:
    'That date is further out than your organization allows. Pick a closer date, or ask a manager to book it for you.',
  DAYS_PER_HUNT_NOT_MET:
    'Hunts at this location are booked in longer blocks. Add days, or ask a manager to book it for you.',
  BIRD_MINIMUM_NOT_MET:
    'This hunt orders fewer birds than your organization requires. Add birds, or ask a manager to book it for you.',
  INSUFFICIENT_STOCK:
    'This hunt orders more birds than your organization has left. Lower the count, or ask a manager to book it for you.',
  LOCATION_INACTIVE: "This location isn't currently active.",
}

export function isHuntConstraintCode(
  value: string | null | undefined,
): value is HuntConstraintCode {
  if (value === null || value === undefined) return false
  return BOOKING_CONSTRAINT_CODES.includes(value as HuntConstraintCode)
}

export function huntConstraintRefusalMessage(
  code: string | null | undefined,
  serverMessage?: string | null,
): string {
  const fromServer = serverMessage?.trim()
  if (fromServer) return fromServer
  const known = isHuntConstraintCode(code) ? REFUSAL_MESSAGES[code] : undefined
  return known ?? GENERIC_REFUSAL_MESSAGE
}

function countOf(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

function cutoffWarningMessage(limit: number | undefined): string {
  if (limit === undefined) {
    return 'This hunt starts inside the booking cutoff. A member could not book it.'
  }
  return `This hunt starts inside the ${countOf(limit, 'hour')} booking cutoff. A member could not book it.`
}

function cutoffLockedWarningMessage(limit: number | undefined): string {
  if (limit === undefined) {
    return 'This hunt is inside the cutoff window. A member could not change or cancel it.'
  }
  return `This hunt starts inside the ${countOf(limit, 'hour')} cutoff window. A member could not change or cancel it.`
}

function activeHuntsWarningMessage(warning: HuntConstraintWarning): string {
  const fromServer = warning.message.trim()
  if (warning.userId !== undefined && fromServer) return fromServer
  const { limit, actual } = warning
  if (limit === undefined || actual === undefined) {
    return 'This booking is over the active hunt limit. A member could not book it.'
  }
  return `You have ${countOf(actual, 'active hunt')} booked and the limit is ${limit}. A member could not book this.`
}

function daysOutWarningMessage(
  limit: number | undefined,
  actual: number | undefined,
): string {
  if (limit === undefined || actual === undefined) {
    return 'This hunt is further out than members are allowed to book.'
  }
  return `This hunt is ${countOf(actual, 'day')} out and members can book ${countOf(limit, 'day')} out.`
}

function daysPerHuntWarningMessage(
  limit: number | undefined,
  actual: number | undefined,
): string {
  if (limit === undefined || actual === undefined) {
    return 'This hunt is shorter than this location is booked in.'
  }
  return `Hunts here are booked ${countOf(limit, 'day')} at a time and this one is ${countOf(actual, 'day')}. A member could not book this.`
}

export function huntConstraintWarningMessage(
  warning: HuntConstraintWarning,
): string {
  switch (warning.code) {
    case 'HUNT_CUTOFF_PASSED':
      return cutoffWarningMessage(warning.limit)
    case 'HUNT_CUTOFF_LOCKED':
      return cutoffLockedWarningMessage(warning.limit)
    case 'TIME_BLOCK_NOT_ALLOWED':
      return 'This time block is not open for booking. A member could not book it.'
    case 'MAX_ACTIVE_HUNTS_REACHED':
      return activeHuntsWarningMessage(warning)
    case 'MAX_DAYS_OUT_EXCEEDED':
      return daysOutWarningMessage(warning.limit, warning.actual)
    case 'DAYS_PER_HUNT_NOT_MET':
      return daysPerHuntWarningMessage(warning.limit, warning.actual)
    default:
      return warning.message
  }
}
